import React from "react";

/**
 * Calibration plot — predicted vs actual frequency per probability bucket.
 * Perfect calibration = diagonal y=x line.
 */
export default function CalibrationPlot({ calibration }) {
  if (!calibration || calibration.length === 0) return null;

  const W = 480;
  const H = 320;
  const PAD = { top: 24, right: 24, bottom: 44, left: 48 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  // Coordinate maps: 0..1 → pixel
  const x = (v) => PAD.left + v * innerW;
  const y = (v) => PAD.top + (1 - v) * innerH;

  // Diagonal (ideal calibration)
  const diag = `M ${x(0)} ${y(0)} L ${x(1)} ${y(1)}`;

  // Filter points that have data
  const pts = calibration.filter((b) => b.predicted != null && b.actual != null);

  // Path for AI line
  let aiPath = "";
  if (pts.length > 1) {
    aiPath = pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.predicted).toFixed(2)} ${y(p.actual).toFixed(2)}`)
      .join(" ");
  }

  const gridTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div data-testid="calibration-plot" className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="block">
        {/* grid */}
        {gridTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={x(t)} x2={x(t)} y1={PAD.top} y2={PAD.top + innerH}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1"
            />
            <line
              x1={PAD.left} x2={PAD.left + innerW} y1={y(t)} y2={y(t)}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1"
            />
            <text
              x={x(t)} y={H - PAD.bottom + 16}
              textAnchor="middle" fill="#71717a"
              fontFamily="IBM Plex Mono, monospace" fontSize="10"
            >
              {Math.round(t * 100)}%
            </text>
            <text
              x={PAD.left - 8} y={y(t) + 3}
              textAnchor="end" fill="#71717a"
              fontFamily="IBM Plex Mono, monospace" fontSize="10"
            >
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}

        {/* axes labels */}
        <text
          x={PAD.left + innerW / 2} y={H - 6}
          textAnchor="middle" fill="#a1a1aa"
          fontFamily="IBM Plex Mono, monospace" fontSize="10"
          letterSpacing="2"
        >
          AI PREDICTED PROBABILITY
        </text>
        <text
          x={14} y={PAD.top + innerH / 2}
          textAnchor="middle" fill="#a1a1aa"
          fontFamily="IBM Plex Mono, monospace" fontSize="10"
          letterSpacing="2"
          transform={`rotate(-90, 14, ${PAD.top + innerH / 2})`}
        >
          ACTUAL FREQUENCY
        </text>

        {/* diagonal: perfect calibration */}
        <path d={diag} stroke="#3f3f46" strokeWidth="1" strokeDasharray="4 4" fill="none" />

        {/* AI line */}
        {aiPath && (
          <path d={aiPath} stroke="#00d8ff" strokeWidth="2" fill="none" />
        )}

        {/* AI points, radius scaled by count */}
        {pts.map((p, i) => {
          const r = 3 + Math.sqrt(Math.max(1, p.count)) * 1.5;
          return (
            <g key={i}>
              <circle cx={x(p.predicted)} cy={y(p.actual)} r={r} fill="#00d8ff" opacity="0.18" />
              <circle cx={x(p.predicted)} cy={y(p.actual)} r={3.5} fill="#00d8ff" />
              <text
                x={x(p.predicted) + 6} y={y(p.actual) - 6}
                fill="#a1a1aa" fontFamily="IBM Plex Mono, monospace" fontSize="9"
              >
                {p.count}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 flex items-center gap-5">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-[2px] w-5 bg-edge" /> AI calibration
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-[2px] w-5 border-t border-dashed border-zinc-500" /> Perfect (y=x)
        </span>
        <span className="text-zinc-500">· Bubble size ∝ count</span>
      </div>
    </div>
  );
}
